// src/components/MainContent/UploadForm/UploadForm.jsx
import React, { useState } from 'react';
import AudioUploader from './AudioUploader';
import SubmitButton from './SubmitButton';
import { uploadToPresignedUrl } from '../../../utility/audioUploader';
import './UploadForm.css';
import axios from 'axios';
import { getAudioResult as getDualAudioResult } from "../../../utility/getResults"
import { getAudioResult as getSingleAudioResult } from "../../../utility/getResultsSingle"
import ResultMessage from '../../message/ResultMessage';

const UploadForm = ({ 
  originalAudioFile, 
  setOriginalAudioFile, 
  candidateAudioFile,
  setCandidateAudioFile
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [isGettingResults, setIsGettingResults] = useState(false);
  const [hasOriginalAudio, setHasOriginalAudio] = useState(false);

  const handleToggleOriginalAudio = () => {
    setHasOriginalAudio(!hasOriginalAudio)
    if (hasOriginalAudio) {
      // Clear original audio file when toggling off
      setOriginalAudioFile(null)
    }
  }

  const handleSubmit = async () => {
    if (!candidateAudioFile) {
      alert("Please provide a Suspect audio file")
      return
    }

    if (hasOriginalAudio && !originalAudioFile) {
      alert("Please provide an original audio file or disable the original audio option")
      return
    }

    try {
      setIsUploading(true)
      setResult(null)
      const backend = "https://3yro7soskz25d3coj7z5mkyv2e0wpahz.lambda-url.ap-south-1.on.aws"

      if (hasOriginalAudio && originalAudioFile) {
        // Dual audio mode
        const originalAudioresponse = await axios.post(
          backend,
          {
            filename: originalAudioFile?.name,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )

        const candidateAudioresponse = await axios.post(
          backend,
          {
            filename: candidateAudioFile?.name,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )

        const originalAudioPresignedUrl = originalAudioresponse.data.write_url
        const candidateAudioPresignedUrl = candidateAudioresponse.data.write_url
        const originalAudioReadUrl = originalAudioresponse.data.read_url
        const candidateAudioReadUrl = candidateAudioresponse.data.read_url

        const originalUploadSuccess = await uploadToPresignedUrl(
          originalAudioPresignedUrl,
          originalAudioFile,
          (progress) => setUploadProgress(progress),
        )

        const candidateUploadSuccess = await uploadToPresignedUrl(
          candidateAudioPresignedUrl,
          candidateAudioFile,
          (progress) => setUploadProgress(progress),
        )

        if (originalUploadSuccess && candidateUploadSuccess) {
          setIsGettingResults(true)
          getDualAudioResult(
            originalAudioReadUrl,
            candidateAudioReadUrl,
            originalAudioFile.name,
            candidateAudioFile.name,
          )
            .then((result) => {
              setOriginalAudioFile(null)
              setCandidateAudioFile(null)
              setIsGettingResults(false)
              if (result) {
                setResult(result)
              } else {
                alert("Failed to get the results. Please try again.")
              }
            })
            .catch((error) => {
              setIsGettingResults(false)
              console.error("Error fetching results:", error)
              alert("An error occurred while fetching results. Please try again.")
            })
        } else {
          alert("Upload failed. Please try again.")
        }
      } else {
        // Single audio mode
        const candidateAudioresponse = await axios.post(
          backend,
          {
            filename: candidateAudioFile?.name,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )

        const candidateAudioPresignedUrl = candidateAudioresponse.data.write_url
        const candidateAudioReadUrl = candidateAudioresponse.data.read_url

        const candidateUploadSuccess = await uploadToPresignedUrl(
          candidateAudioPresignedUrl,
          candidateAudioFile,
          (progress) => setUploadProgress(progress),
        )

        if (candidateUploadSuccess) {
          setIsGettingResults(true)
          getSingleAudioResult(candidateAudioReadUrl, candidateAudioFile.name)
            .then((result) => {
              setCandidateAudioFile(null)
              setIsGettingResults(false)
              if (result) {
                setResult(result)
              } else {
                alert("Failed to get the results. Please try again.")
              }
            })
            .catch((error) => {
              setIsGettingResults(false)
              console.error("Error fetching results:", error)
              alert("An error occurred while fetching results. Please try again.")
            })
        } else {
          alert("Upload failed. Please try again.")
        }
      }
    } catch (error) {
      console.error("Error:", error)
      alert("An error occurred. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  
  return (
    <div className="form-section">
    <h4 className="form-title">Leading the Fight Against AI-Generated Voice Fraud in India</h4>
      <div className="upload-container">
        <AudioUploader
          name="Upload Suspect Audio"
          audioFile={candidateAudioFile}
          setAudioFile={setCandidateAudioFile}
          disabled={isUploading}
        />

        {/* Original audio uploader - shown conditionally */}
        {hasOriginalAudio && (
          <div className="upload-container">
            <AudioUploader
              name="Upload Original Audio"
              audioFile={originalAudioFile}
              setAudioFile={setOriginalAudioFile}
              disabled={isUploading}
            />
          </div>
        )}
        
        {/* Toggle for original audio */}
        <div className="original-audio-toggle">
          <label className="toggle-container">
            <input
              type="checkbox"
              checked={hasOriginalAudio}
              onChange={handleToggleOriginalAudio}
              disabled={isUploading}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">Have a original audio?</span>
          </label>
        </div>


        {isUploading ? (
          <div className="upload-progress-container">
            <div className="loader"></div>
            <p>Uploading: {uploadProgress}%</p>
            <progress value={uploadProgress} max="100"></progress>
          </div>
        ) : (
          <SubmitButton 
            handleSubmit={handleSubmit} 
            disabled={!candidateAudioFile || (hasOriginalAudio && !originalAudioFile) || isUploading || isGettingResults}
          />
        )}
        {isGettingResults && (
        <div className="getting-results-container">
            <div className="loader"></div>
            <div className="getting-results-text">Getting results for you...</div>
          </div>
        )}
        {result && <ResultMessage result={result} />}
      </div>
    </div>
  );
};

export default UploadForm;